import { useCallback, useEffect, useMemo, useState } from 'react';
import { getMeFavoriteItemsV1 } from '@/services/platform/api/me-—-favorites/me-—-favorites';
import { useFavoriteLists } from '@/services/platform/data/shop';

export interface UseEventFavoriteIndexResult {
  /** The user's 'Events' favorite lists. */
  eventFavoriteLists: ReturnType<typeof useFavoriteLists>['data'];
  /** Event id -> the favorite list containing it, for every favorited event. */
  favoriteListGuidByEventId: Map<string, string>;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Batch-resolves which Events favorite list (if any) contains each event, for an entire
 * screen (calendar, search results) in one shot. Mirrors `useShopFavoriteIndex` — fetches
 * each Events list's items once instead of one `/favorites/lookup` request per event card.
 */
export function useEventFavoriteIndex(): UseEventFavoriteIndexResult {
  const { data: favoriteLists, refetch: refetchLists } = useFavoriteLists();
  const eventFavoriteLists = useMemo(
    () => favoriteLists.filter(l => l.listType === 'Events'),
    [favoriteLists]
  );
  const eventFavoriteListsWithGuid = useMemo(
    () => eventFavoriteLists.filter((l): l is typeof l & { publicGuid: string } => l.publicGuid != null),
    [eventFavoriteLists]
  );

  const [favoriteListGuidByEventId, setFavoriteListGuidByEventId] = useState<Map<string, string>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const refetchItems = useCallback(async () => {
    if (eventFavoriteListsWithGuid.length === 0) {
      setFavoriteListGuidByEventId(new Map());
      return;
    }
    setIsLoading(true);
    try {
      const responses = await Promise.all(
        eventFavoriteListsWithGuid.map(list => getMeFavoriteItemsV1(list.publicGuid))
      );
      const map = new Map<string, string>();
      responses.forEach((response, index) => {
        if (response.status !== 200) return;
        const listGuid = eventFavoriteListsWithGuid[index].publicGuid;
        response.data.forEach(item => {
          if (item.itemType === 'event' && item.itemExternalId) {
            map.set(item.itemExternalId, listGuid);
          }
        });
      });
      setFavoriteListGuidByEventId(map);
    } finally {
      setIsLoading(false);
    }
  }, [eventFavoriteListsWithGuid]);

  useEffect(() => {
    refetchItems();
  }, [refetchItems]);

  const refetch = useCallback(async () => {
    await refetchLists();
    await refetchItems();
  }, [refetchLists, refetchItems]);

  return { eventFavoriteLists, favoriteListGuidByEventId, isLoading, refetch };
}
